import { useEffect, useState } from 'react';

/**
 * Debounces a value that updates frequently.
 * Returns the debounced value after the specified delay.
 *
 * Use this when you need to delay the propagation of a changing value.
 * For debouncing function calls, use `useDebouncedCallback` instead.
 * For search with state management, use `useDebouncedSearch` instead.
 *
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (default: 300ms)
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * // Debouncing a search query value
 * const [searchQuery, setSearchQuery] = useState('');
 * const debouncedQuery = useDebounce(searchQuery, 500);
 *
 * useEffect(() => {
 *   if (debouncedQuery) {
 *     fetchSearchResults(debouncedQuery);
 *   }
 * }, [debouncedQuery]);
 *
 * <input
 *   value={searchQuery}
 *   onChange={(e) => setSearchQuery(e.target.value)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Debouncing window resize for expensive calculations
 * const [windowWidth, setWindowWidth] = useState(window.innerWidth);
 * const debouncedWidth = useDebounce(windowWidth, 300);
 *
 * useEffect(() => {
 *   // Expensive layout recalculation only happens after debounce
 *   recalculateLayout(debouncedWidth);
 * }, [debouncedWidth]);
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, delay]);

  return debouncedValue;
}
